import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useRootStore, baseGet, basePost } from '@qsrsoft/myq-core';
import { OVERRIDABLE_FIELDS } from '@/components/admin/catalogLocales';

const APP_NAME = 'catalogOverrides';

// Piggy-backs the existing organization/${orgKey}/settings endpoint but uses a
// synthetic org key of `enterprise-<enterpriseClient>-<countryCode>` so
// overrides scope to the (enterprise, country) tuple — e.g. McDonald's US vs
// McDonald's CA can carry different labels. See plan file for the tradeoff.
function buildRoute(scopeKey) {
  return `organization/enterprise-${scopeKey}/settings?appName=${APP_NAME}`;
}

// The settings API returns the whole app document (every catalog's overrides,
// keyed by catalog path) when `settingName` is omitted. Older gateways
// double-encoded that branch, so tolerate a JSON string as well as an object.
function parseDocument(data) {
  let doc = data;
  if (typeof doc === 'string') {
    try {
      doc = JSON.parse(doc);
    } catch {
      return {};
    }
  }
  return doc && typeof doc === 'object' && !Array.isArray(doc) ? doc : {};
}

export const useCatalogOverridesStore = defineStore('catalogOverrides', () => {
  const rootStore = useRootStore();
  const { enterpriseClient, countryCode } = storeToRefs(rootStore);

  const enterpriseKey = computed(() => {
    const ec = enterpriseClient.value;
    if (!ec) return null;
    const cc = countryCode.value;
    const enterprisePart = String(ec).toLowerCase();
    const countryPart = cc ? String(cc).toLowerCase() : 'unknown';
    return `${enterprisePart}-${countryPart}`;
  });

  const overridesByCatalog = ref({});
  // One in-flight/settled fetch of the whole override document per enterprise
  // key. Every catalog's load() shares it, so a page costs one request rather
  // than one per catalog it renders.
  let documentPromise = null;
  let documentKey = null;

  function getOverridesFor(catalogPath) {
    return overridesByCatalog.value[catalogPath] || {};
  }

  function isLoaded(catalogPath) {
    return Object.prototype.hasOwnProperty.call(overridesByCatalog.value, catalogPath);
  }

  function loadDocument(key) {
    if (documentPromise && documentKey === key) return documentPromise;
    documentKey = key;
    documentPromise = (async () => {
      try {
        const doc = parseDocument(await baseGet(buildRoute(key)));
        const entries = {};
        for (const [path, value] of Object.entries(doc)) {
          entries[path] = value && typeof value === 'object' ? value : {};
        }
        // Anything saved while the fetch was in flight wins over the fetched copy.
        overridesByCatalog.value = { ...entries, ...overridesByCatalog.value };
        return entries;
      } catch (err) {
        // Treat any load failure as "no overrides"; load() caches the empty
        // result per catalog so we don't retry every mount.
        console.warn('[catalogOverrides] load failed', err);
        return {};
      }
    })();
    return documentPromise;
  }

  async function load(catalogPath) {
    if (!catalogPath) return {};
    if (isLoaded(catalogPath)) return overridesByCatalog.value[catalogPath];

    const key = enterpriseKey.value;
    if (!key) {
      overridesByCatalog.value = { ...overridesByCatalog.value, [catalogPath]: {} };
      return {};
    }

    const doc = await loadDocument(key);
    if (!isLoaded(catalogPath)) {
      overridesByCatalog.value = {
        ...overridesByCatalog.value,
        [catalogPath]: doc[catalogPath] || {},
      };
    }
    return overridesByCatalog.value[catalogPath];
  }

  async function save(catalogPath, overrides) {
    const key = enterpriseKey.value;
    if (!key) throw new Error('No enterpriseClient available; cannot save catalog overrides');
    const route = `${buildRoute(key)}&settingName=${encodeURIComponent(catalogPath)}`;
    const cleaned = pruneEmpty(overrides);
    await basePost(route, cleaned);
    overridesByCatalog.value = { ...overridesByCatalog.value, [catalogPath]: cleaned };
    return cleaned;
  }

  async function remove(catalogPath, field) {
    const current = { ...getOverridesFor(catalogPath) };
    delete current[field];
    return save(catalogPath, current);
  }

  // Clear every override for a catalog. Posts null (the settings API's
  // delete-by-null convention) so the Dynamo row goes away entirely rather
  // than staying around as an empty document.
  async function resetCatalog(catalogPath) {
    const key = enterpriseKey.value;
    if (!key) throw new Error('No enterpriseClient available; cannot reset catalog overrides');
    const route = `${buildRoute(key)}&settingName=${encodeURIComponent(catalogPath)}`;
    await basePost(route, null);
    overridesByCatalog.value = { ...overridesByCatalog.value, [catalogPath]: {} };
    return {};
  }

  function pruneEmpty(overrides) {
    const out = {};
    for (const [field, entry] of Object.entries(overrides || {})) {
      if (!entry || typeof entry !== 'object') continue;
      const kept = {};
      for (const k of OVERRIDABLE_FIELDS) {
        const v = entry[k];
        if (typeof v === 'string' && v.trim() !== '') kept[k] = v;
      }
      if (entry.hidden === true) kept.hidden = true;
      if (Object.keys(kept).length) out[field] = kept;
    }
    return out;
  }

  return {
    // state
    overridesByCatalog,
    // getters
    enterpriseKey,
    // actions
    load,
    save,
    remove,
    resetCatalog,
    getOverridesFor,
    isLoaded,
    // constants
    OVERRIDABLE_FIELDS,
  };
});
